import PropTypes from 'prop-types';
import React from 'react';
import FormControl from 'react-bootstrap/FormControl';
import { MaterialCheckbox } from '@everfi/reactifi';
import { MaterialInput } from 'lib/forms/material_input';
import { MaterialSelect } from 'lib/forms/material_select';
import QuestionAnswersForm from './QuestionAnswersForm';
import i18n from "lib/utility/i18n";
import LanguageFieldWrapper from 'lib/components/LanguageFieldWrapper';
import LocaleHelper from 'lib/utility/locale_helper';
import AuthTokenHelper from 'lib/utility/auth_token_helper';

export default class QuestionForm extends React.Component {
  static propTypes = {
    question: PropTypes.object.isRequired,
    action: PropTypes.string.isRequired,
    accountQuestion: PropTypes.object,
    redirectHash: PropTypes.string,
    location: PropTypes.oneOf(['registration', 'demographic']),
    accountId: PropTypes.string,
    level: PropTypes.string,
    questionTypes: PropTypes.array,
    predefinedQuestions: PropTypes.array,
    displayLocation: PropTypes.array,
    showEnabledCheckbox: PropTypes.bool,
    showRequiredCheckbox: PropTypes.bool,
    showIsSegmentCheckbox: PropTypes.bool,
    showAllowFromUrlCheckbox: PropTypes.bool,
    showEnableSsoCheckbox: PropTypes.bool,
    showLocationSelector: PropTypes.bool,
    showAnswers: PropTypes.bool,
    answers: PropTypes.array,
    showRightRail: PropTypes.bool,
    isAccountQuestion: PropTypes.bool
  };

  static defaultProps = {
    location: 'registration',
    questionTypes: [['Radio', 'radio'], ['Text', 'text'], ['Drop Down', 'drop-down']],
    predefinedQuestions: [],
    answers: [],
    showEnabledCheckbox: true,
    showRequiredCheckbox: true,
    showIsSegmentCheckbox: true,
    showAllowFromUrlCheckbox: true,
    showLocationSelector: false,
    showAnswers: true,
    showEnableSsoCheckbox: true
  };

  constructor(props) {
    super(props);

    this.state = {
      showAnswers: props.showAnswers,
      question: {
        ...(props.question || {}),
        // Seed allow_from_url from accountQuestion so allowFromUrl()-driven UI
        // (slug hint, warnings, option filtering) is correct on initial render
        // when editing an existing account question.
        allow_from_url: (props.accountQuestion && props.accountQuestion.allow_from_url) || (props.question && props.question.allow_from_url) || false
      },
      accountQuestion: props.accountQuestion || {},
      location: props.location || "registration",
      allowFromUrl: (props.accountQuestion && props.accountQuestion.allow_from_url) || false,
      required: (props.accountQuestion && props.accountQuestion.required) || false,
      is_segment: (props.accountQuestion && props.accountQuestion.is_segment) || false,
      sso_attribute_name: (props.accountQuestion && props.accountQuestion.sso_attribute_name) || null,
      enable_sso_question: (props.accountQuestion && props.accountQuestion.enable_sso_question) || false
    }

    this.updateEnableSso = this.updateEnableSso.bind(this);
    this.updateSlug = this.updateSlug.bind(this);
    this.updateRequired = this.updateRequired.bind(this);
    this.updateIsSegment = this.updateIsSegment.bind(this);
    this.allowFromUrl = this.allowFromUrl.bind(this);
    this.updateQuestionSelect = this.updateQuestionSelect.bind(this);
  }

  isNew() {
    return (this.state.accountQuestion.id === null || this.state.accountQuestion.id === undefined)
  }

  allowFromUrl() {
    const { accountQuestion, question } = this.state
    if (this.isDemographicQuestion()) {
      return (accountQuestion && accountQuestion.allow_from_url) || false
    }
    return (question && question.allow_from_url) || false
  }

  isRegistrationQuestion() {
    return this.state.location === "registration" || this.props.predefinedQuestions.length === 0
  }

  isDemographicQuestion() {
    return this.state.location === "demographic" && this.props.predefinedQuestions.length > 0
  }

  fieldName(field) {
    if (this.props.accountQuestion)
      return "account_question[" + field + "]";
    else if (['enabled', 'required', 'allow_from_url', 'display_location',
      'sso_attribute_name', 'enable_sso_question', 'is_segment'].indexOf(field) !== -1)
      return field
    else
      return "question[" + field + "]";
  }

  updateSlug(event) {
    const val = event.target.value
    const newQuestion = Object.assign({}, this.state.question);
    newQuestion.slug = val;
    this.setState({ question: newQuestion });
  }

  updateAllow = (checked) => {
    const { accountQuestion, question } = this.state

    if (this.isDemographicQuestion()) {
      // Demographic: allow_from_url lives on accountQuestion
      const newAccountQuestion = Object.assign({}, accountQuestion);
      newAccountQuestion.allow_from_url = checked;
      this.setState({ accountQuestion: newAccountQuestion });
    } else if (this.props.accountQuestion) {
      // Registration account question: update accountQuestion so the checkbox
      // stays in sync (checked={this.state.accountQuestion.allow_from_url}),
      // and also update question.allow_from_url so allowFromUrl()-derived UI
      // (slug hint, warnings, option filtering) stays consistent.
      // Pin question_type to 'text' since Allow From URL only supports text.
      const newAccountQuestion = Object.assign({}, accountQuestion);
      newAccountQuestion.allow_from_url = checked;
      const newQuestion = Object.assign({}, question);
      newQuestion.allow_from_url = checked;
      if (checked) {
        newQuestion.question_type = 'text';
      }
      this.setState({ accountQuestion: newAccountQuestion, question: newQuestion });
    } else {
      // New registration question (no accountQuestion prop): allow_from_url lives on question
      const newQuestion = Object.assign({}, question);
      newQuestion.allow_from_url = checked;
      if (checked) {
        newQuestion.question_type = 'text';
      }
      this.setState({ question: newQuestion });
    }
  }

  updateRequired(checked) {
    this.setState({ required: checked });
  }

  updateIsSegment(checked) {
    this.setState({ is_segment: checked });
  }

  updateEnableSso(checked) {
    this.setState({ showEnableSsoCheckbox: checked });
  }

  updateQuestionSelect(event) {
    const id = event.target.value
    const { predefinedQuestions } = this.props
    if (id !== "") {
      let question = predefinedQuestions.find(question => question.id === parseInt(id))
      this.setState({ question })
    } else {
      this.setState({ showAnswers: true, question: {} })
    }
  }

  renderSlugName() {
    const { question } = this.state
    if (this.allowFromUrl() && question.slug) {
      if (this.isRegistrationQuestion()) {
        return (<div>Slug for URL-provided answers: <em>{question.slug}</em></div>)
      } else if (this.isDemographicQuestion() && question.question_type === 'text') {
        return (<div>Slug for URL-provided answers: <em>{question.slug}</em></div>)
      }
    }
  }

  isAdminQuestionPath() {
    return window.location.pathname.indexOf('accounts') === -1;
  }


  renderSlugField() {
    const { question } = this.state

    if (this.isRegistrationQuestion()) {
      return (
        <MaterialInput
          data-object-type="question-slug"
          label={i18n.t("Slug")}
          type="text"
          required={true}
          name={this.fieldName("slug")}
          onChange={this.updateSlug}
          defaultValue={this.state.question.slug}
          wrapperClassName="fg-line fg-populated" />
      )
    } else if (this.isDemographicQuestion() && question.question_type === 'text' && !this.props.isAccountQuestion) {
      return (
        <MaterialInput
          data-object-type="question-slug"
          label={i18n.t("Slug")}
          type="text"
          required={true}
          name={this.fieldName("slug")}
          onChange={this.updateSlug}
          defaultValue={this.state.question.slug}
          wrapperClassName="fg-line fg-populated" />
      )
    }
    return null;
  }

  renderQuestionType() {
    const { question, accountQuestion } = this.state
    const allow_from_url = question?.allow_from_url ?? accountQuestion?.allow_from_url ?? false

    if (this.isRegistrationQuestion()) {
      let options = this.props.questionTypes.map(option => {
        return <option key={option[1]} value={option[1]}>{option[0]}</option>
      });

      if (allow_from_url) {
        options = <option key={this.props.questionTypes[1][1]} value={this.props.questionTypes[1][1]}>{this.props.questionTypes[1][0]}</option>
      }

      return (<MaterialSelect
        data-object-type="question-question_type"
        key={`question_type_${allow_from_url}`}
        label={i18n.t("Question Type")}
        type="select"
        required={true}
        defaultValue={this.state.question.question_type}
        disabled={this.disabledByLocale}
        disabledByLocale={this.disabledByLocale}
        wrapperClassName="fg-line fg-populated"
        ref="question_type"
        name={this.fieldName("question_type")}
        onChange={() => { this.forceUpdate() }}>
        <option value="" />
        {options}
      </MaterialSelect>)
    }
  }

  renderDisplayLocation() {
    let options = this.props.displayLocation.map(option => {
      return <option key={option[1]} value={option[1]}>{option[0]}</option>
    });

    return (<MaterialSelect
      data-object-type="question-location"
      label={i18n.t("Where should this question display?")}
      type="select"
      defaultValue={this.state.accountQuestion.display_location}
      disabled={this.disabledByLocale}
      disabledByLocale={this.disabledByLocale}
      wrapperClassName="fg-line fg-populated"
      ref="display_location"
      name={this.fieldName("display_location")}
      onChange={() => { this.forceUpdate() }}>
      {options}
    </MaterialSelect>)
  }

  renderPositionField() {
    if (!this.props.accountQuestion || !this.isRegistrationQuestion()) {
      return null;
    }

    return (<MaterialInput
      data-object-type="question-position"
      label={i18n.t("Position")}
      type="number"
      min={1}
      step={1}
      name={this.fieldName("position")}
      defaultValue={this.state.accountQuestion.position || 1}
      wrapperClassName="fg-line fg-populated" />)
  }

  renderAllowedQuestions() {
    if (this.isDemographicQuestion()) {
      let options = this.props.predefinedQuestions.map(qa => {
        return <option key={qa.id} value={qa.id}>{qa.question_text}</option>
      })

      return (<MaterialSelect
        data-object-type="question-question_id"
        label={i18n.t("Question")}
        type="select"
        defaultValue={this.state.question.id}
        wrapperClassName="fg-line fg-populated"
        onChange={this.updateQuestionSelect}
        name={this.fieldName("question_id")}>
        <option value="" />
        {options}
      </MaterialSelect>)
    }
  }

  renderAnswers() {
    let questionType = this.refs.question_type ?
      this.refs.question_type.getValue() : this.state.question.question_type;

    if (this.state.showAnswers && this.props.answers && questionType && questionType !== 'text') {
      return (<div className="card-body">
        <QuestionAnswersForm
          allowNewAnswer={this.isDemographicQuestion() ? false : true}
          answers={this.props.answers}
          editable={this.isDemographicQuestion() ? false : true}
          ref="answers"
          question={this.props.question}
        />
      </div>)
    }
  }

  renderRightRail() {
    if (this.props.showRightRail) {
      return (
        <div>
          <div className="col-md-3">
            <LanguageFieldWrapper />
          </div>
        </div>
      )
    }
  }

  mainFormWidth() {
    return (this.props.showRightRail ? 'col-md-9' : 'col-md-12')
  }

  get disabledByLocale() {
    return LocaleHelper.formLocale !== 'en';
  }

  get disabledByLocaleClass() {
    return this.disabledByLocale ? LocaleHelper.disabledByLocaleClass : null;
  }

  get disabledFieldsMessage() {
    if (this.disabledByLocale) {
      return (
        <div className="disabled-by-locale">
          <p>The following fields are disabled when editing the non-default language.</p>
        </div>
      )
    }
  }

  render() {
    return (
      <div>
        <div className="row">
          <form className="simple_form form-material form-container question-form" action={this.props.action} method="post">
            <div className={this.props.showRightRail ? 'form-setup-translate' : null}>

              <div className={this.mainFormWidth()}>
                <FormControl type="hidden" name="authenticity_token" value={AuthTokenHelper.getAuthToken} />
                {this.props.level ? <FormControl type="hidden" name="level" value={this.props.level} /> : null}
                {!this.isNew() ? <FormControl type="hidden" name="_method" value="patch" /> : null}
                {this.props.redirectHash ?
                  <FormControl type="hidden" name="_redirect_hash" value={"#" + this.props.redirectHash} /> : null}
                {this.props.accountId ?
                  <FormControl type="hidden" name="account_id" value={this.props.accountId} /> : null}

                {this.isRegistrationQuestion() ? <MaterialInput
                  data-object-type="question-description"
                  label={i18n.t("Description")}
                  type="text"
                  name={this.fieldName("description")}
                  defaultValue={this.state.question.description}
                  wrapperClassName="fg-line fg-populated" /> : null}

                {this.renderSlugField()}

                {this.renderAllowedQuestions()}

                {this.isRegistrationQuestion() ? <MaterialInput
                  data-object-type="question-question_text"
                  label={i18n.t("Question Text")}
                  type="text"
                  required={true}
                  name={this.fieldName("question_text")}
                  defaultValue={this.state.question.question_text}
                  wrapperClassName="fg-line fg-populated" /> : null}

                {this.renderQuestionType()}
                {this.renderAnswers()}

                {this.disabledFieldsMessage}

                {this.props.showLocationSelector ? <MaterialSelect
                  label={i18n.t("Location")}
                  type="select"
                  defaultValue={this.state.location}
                  disabled={this.disabledByLocale}
                  disabledByLocale={this.disabledByLocale}
                  wrapperClassName="fg-line fg-populated hidden"
                  name={this.fieldName("location")}>
                  <option key="registration" value="registration">Registration</option>
                </MaterialSelect> : null}

                {this.renderDisplayLocation()}
                {this.renderPositionField()}

                {this.props.showEnabledCheckbox ? <div><MaterialCheckbox
                  data-object-type="question-enabled"
                  label={i18n.t("Enabled")}
                  name={this.fieldName('enabled')}
                  disabled={this.disabledByLocale}
                  value={true}
                  checked={this.state.accountQuestion.enabled} /></div> : null}

                {this.props.showRequiredCheckbox ? <div><MaterialCheckbox
                  data-object-type="question-required"
                  label={i18n.t("Required")}
                  name={this.fieldName('required')}
                  disabled={this.disabledByLocale}
                  ref="required_checkbox"
                  onChange={this.updateRequired}
                  value={true}
                  checked={this.state.accountQuestion.required} /></div> : null}

                {this.props.showIsSegmentCheckbox ? <div><MaterialCheckbox
                  data-object-type="question-is_segment"
                  label={i18n.t("Used to segment a shared program")}
                  name={this.fieldName('is_segment')}
                  disabled={this.disabledByLocale}
                  ref="is_segment_checkbox"
                  onChange={this.updateRequired}
                  value={true}
                  checked={this.state.accountQuestion.is_segment} /></div> : null}

                {this.props.showAllowFromUrlCheckbox ? <div><MaterialCheckbox
                  data-object-type="question-allow_from_url"
                  label={i18n.t("Allow From URL")}
                  name={this.fieldName('allow_from_url')}
                  disabled={this.disabledByLocale}
                  value={true}
                  ref="allow_from_url"
                  onChange={this.updateAllow}
                  checked={this.state.accountQuestion.allow_from_url} /></div> : null}

                {this.renderSlugName()}

                {(this.allowFromUrl() && this.state.required) ?
                  <div className="alert alert-danger">Allow From URL is mainly used for external parameters. It should NOT be a required attribute.</div> : null}

                {this.props.showEnableSsoCheckbox ? <MaterialCheckbox
                  data-object-type="question-enable_sso_question"
                  label={i18n.t("Enable SSO")}
                  name={this.fieldName('enable_sso_question')}
                  disabled={this.disabledByLocale}
                  value={true}
                  ref="enable_sso_question"
                  onChange={this.updateEnableSso}
                  checked={this.state.accountQuestion.enable_sso_question} /> : null}

                {this.isRegistrationQuestion() ? <MaterialInput
                  data-object-type="question-sso_attribute_name"
                  label={i18n.t("SSO Attribute Name")}
                  type="text"
                  name={this.fieldName("sso_attribute_name")}
                  help="The SSO attribute must match EXACTLY to the SSO attribute name provided by the SSO solution."
                  defaultValue={this.state.accountQuestion.sso_attribute_name}
                  wrapperClassName="fg-line fg-populated" /> : null}

                {(this.state.enable_sso_question && this.state.required) ?
                  <div className="alert alert-dismissible alert-danger">
                    Enable SSO should not be checked while required is checked. SSO Questions should not be required.
                  </div> : null}

              </div>

              {this.renderRightRail()}

              <div className="row">
                <div className="col-md-12">
                  <div className="form-actions">
                    <input type="submit" name="commit" value={i18n.t("Save")} className="btn btn-primary" />
                  </div>
                </div>
              </div>

            </div>
          </form>
        </div>
      </div>
    )
  }
}
